import React from 'react';
import Row from '@amzn/meridian/row';
import Button from '@amzn/meridian/button';
import Select, { SelectOption } from '@amzn/meridian/select';
import Text from '@amzn/meridian/text';

interface CursorPaginationProps {
  /** Current page number (1-based, for display) */
  page: number;
  pageSize: number;
  pageSizeOptions?: number[];
  hasMore: boolean;
  /** Whether a page fetch is in progress */
  isLoading?: boolean;
  onNextPage: () => void;
  onPreviousPage: () => void;
  onPageSizeChange: (pageSize: number) => void;
}

/**
 * Cursor-based pagination component. Shows Previous/Next buttons instead of
 * page numbers — designed for large datasets where computing total count is expensive.
 */
export const CursorPagination: React.FC<CursorPaginationProps> = ({
  page,
  pageSize,
  pageSizeOptions = [10, 25, 50, 100],
  hasMore,
  isLoading,
  onNextPage,
  onPreviousPage,
  onPageSizeChange,
}) => {
  return (
    <Row alignmentHorizontal="end" alignmentVertical="center" spacing="300">
      <Select
        data-testid="cursor-pagination-select"
        size="small"
        value={pageSize}
        onChange={onPageSizeChange}
      >
        {pageSizeOptions.map((option) => (
          <SelectOption
            key={option}
            label={`${option} Items`}
            value={option}
          />
        ))}
      </Select>
      <Text type="b300">Page {page}</Text>
      <Button
        data-testid="cursor-pagination-prev"
        disabled={page <= 1 || isLoading}
        size="small"
        type="secondary"
        onClick={onPreviousPage}
      >
        Previous
      </Button>
      <Button
        data-testid="cursor-pagination-next"
        disabled={!hasMore || isLoading}
        size="small"
        type="primary"
        onClick={onNextPage}
      >
        Next page
      </Button>
    </Row>
  );
};
